import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Receipt, Printer, Wallet } from "lucide-react";
import api, { formatPrice } from "@/lib/api";

const SHIFT_START = 6; // смена: с 06:00 до 06:00 следующего дня

const localDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const shiftDayToday = () => {
  const d = new Date();
  if (d.getHours() < SHIFT_START) d.setDate(d.getDate() - 1);
  return localDay(d);
};

const shiftDate = (day, delta) => {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return localDay(d);
};

export default function ShiftSummary() {
  const [day, setDay] = useState(shiftDayToday);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/shift/summary?date=${day}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [day]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const printReceipt = (id) => {
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    frame.src = `/admin/print/session/${id}`;
    document.body.appendChild(frame);
    setTimeout(() => frame.remove(), 60000);
  };

  const isToday = day === shiftDayToday();
  const dayLabel = new Date(`${day}T12:00:00`).toLocaleDateString("ru-RU", { day: "2-digit", month: "long", weekday: "short" });

  const cells = data ? [
    { label: "Закрыто чеков", value: data.receipts_count, testid: "shift-receipts-count" },
    { label: "Выручка смены", value: formatPrice(data.revenue), accent: true, testid: "shift-revenue" },
    { label: "Домики (время)", value: formatPrice(data.venue_time_total), testid: "shift-venue-time" },
    { label: "Доп. гости", value: formatPrice(data.extra_guests_total), testid: "shift-extra-guests" },
    { label: "Кухня и бар", value: formatPrice(data.kitchen_total), testid: "shift-kitchen" },
    { label: "Услуги", value: formatPrice(data.services_total), testid: "shift-services" },
    { label: "Средний чек", value: formatPrice(data.avg_receipt), testid: "shift-avg" },
    { label: `Предоплаты Kaspi (${data.prepayments_count})`, value: formatPrice(data.prepayments_total), testid: "shift-prepayments" },
  ] : [];

  return (
    <div className="bg-card border border-border p-5 mb-8" data-testid="shift-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="font-sans font-semibold text-sm uppercase tracking-widest flex items-center gap-2">
          <Wallet className="w-4 h-4 text-primary" /> Итог смены
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setDay(shiftDate(day, -1))} className="p-1 text-muted-foreground hover:text-primary" data-testid="shift-prev-day">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm" data-testid="shift-day-label">{dayLabel}{isToday ? " · сегодня" : ""}</span>
          <button onClick={() => setDay(shiftDate(day, 1))} disabled={isToday} className="p-1 text-muted-foreground hover:text-primary disabled:opacity-30" data-testid="shift-next-day">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-muted-foreground">Загрузка...</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">
            Смена с 06:00 {dayLabel} до 06:00 следующего дня
            {data.open_places_now > 0 ? ` · сейчас открыто мест: ${data.open_places_now}` : ""}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {cells.map(c => (
              <div key={c.label} className="bg-background border border-border p-3" data-testid={c.testid}>
                <p className="text-xs text-muted-foreground mb-1">{c.label}</p>
                <p className={`font-serif ${c.accent ? "text-2xl text-primary" : "text-lg"}`}>{c.value}</p>
              </div>
            ))}
          </div>

          {data.receipts.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="shift-receipts-empty">За эту смену чеков пока нет</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="shift-receipts-list">
              {data.receipts.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-3 border-b border-border pb-2" data-testid={`shift-receipt-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm truncate flex items-center gap-1.5">
                      <Receipt className="w-3.5 h-3.5 text-primary shrink-0" /> {r.venue_name || "—"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.closed_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                      {r.bath_minutes ? ` · ${r.bath_minutes} мин` : ""}
                      {r.orders_total ? ` · заказы ${formatPrice(r.orders_total)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-primary text-sm">{formatPrice(r.total_amount)}</span>
                    <Button size="sm" variant="outline" onClick={() => printReceipt(r.id)} className="rounded-none gap-1 text-xs" data-testid={`shift-print-${r.id}`}>
                      <Printer className="w-3 h-3" /> Печать
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
